import { WHISPER_CONFIG } from '../../config/whisper';
import { CaptureSource, CaptureSourceCallbacks } from './types';

/**
 * Make-before-break overlap window: the NEXT recorder starts this long before
 * the current one stops, because MediaRecorder.stop() truncates ~40 ms of tail
 * audio and the overlap must exceed that with margin. 200 ms is spike-verified
 * safe; 50 ms is spike-verified UNSAFE (it produced gaps). Values in between
 * are untested — do not tune down without re-measuring; the failure is a
 * silent capture gap.
 */
export const ROTATION_OVERLAP_MS = 200;

interface ActiveTake {
  recorder: MediaRecorder;
  /** Resolves once this take's blob has been emitted (or it ended with no data). */
  settled: Promise<void>;
}

/**
 * Strategy A fallback for containers with no registered ContainerCutter: one
 * stream, alternating MediaRecorders rotated make-before-break so no audio is
 * lost at the seams. Each take is a complete self-contained file — no byte
 * cutting, so no container knowledge — at the cost of ~ROTATION_OVERLAP_MS of
 * duplicated (double-transcribed) audio per seam.
 */
export class RotatingOverlapSource implements CaptureSource {
  private stream: MediaStream | null = null;
  private takes: ActiveTake[] = [];
  private rotationTimer: number | null = null;
  private overlapStopTimer: number | null = null;

  constructor(
    private readonly mimeType: string,
    private readonly callbacks: CaptureSourceCallbacks
  ) {}

  start(stream: MediaStream): void {
    if (this.takes.some((take) => take.recorder.state === 'recording')) {
      throw new Error('RotatingOverlapSource is already capturing');
    }
    this.stream = stream;
    this.startTake();
  }

  async stop(): Promise<void> {
    this.clearTimers();
    this.stream = null;
    // Stop in start order so takes emit in capture order — chunk numbers are
    // assigned at emission.
    for (const take of [...this.takes]) {
      if (take.recorder.state === 'recording') {
        take.recorder.stop();
      }
      await take.settled;
    }
  }

  async switchStream(stream: MediaStream): Promise<void> {
    await this.stop();
    this.start(stream);
  }

  private startTake(): void {
    const stream = this.stream;
    if (!stream) {
      throw new Error('RotatingOverlapSource has no stream');
    }
    const recorder = new MediaRecorder(stream, {
      mimeType: this.mimeType,
      audioBitsPerSecond: WHISPER_CONFIG.audioConfig.audioBitsPerSecond,
    });
    const chunks: Blob[] = [];

    let settle!: () => void;
    const settled = new Promise<void>((resolve) => {
      settle = resolve;
    });
    const take: ActiveTake = { recorder, settled };

    recorder.ondataavailable = (event: BlobEvent) => {
      if (event.data && event.data.size > 0) {
        chunks.push(event.data);
      }
    };
    recorder.onstop = () => {
      this.takes = this.takes.filter((active) => active !== take);
      if (chunks.length > 0) {
        this.callbacks.onChunk({
          blob: new Blob(chunks, { type: this.mimeType }),
          captureEndMs: Date.now(),
        });
      }
      settle();
    };
    recorder.onerror = (event) => {
      const error = (event as unknown as { error?: unknown }).error;
      this.callbacks.onError(
        error instanceof Error
          ? error
          : new Error('MediaRecorder error during rotating capture')
      );
    };

    recorder.start();
    this.takes.push(take);
    this.scheduleRotation(take);
  }

  /**
   * Make-before-break: the NEXT take's recorder is constructed and started
   * ROTATION_OVERLAP_MS BEFORE this take's recorder is stopped, so the ~40 ms
   * stop-tail truncation falls inside audio the next recorder is already
   * capturing.
   */
  private scheduleRotation(current: ActiveTake): void {
    this.rotationTimer = window.setTimeout(() => {
      this.rotationTimer = null;
      this.startTake();
      this.overlapStopTimer = window.setTimeout(() => {
        this.overlapStopTimer = null;
        if (current.recorder.state === 'recording') {
          current.recorder.stop();
        }
      }, ROTATION_OVERLAP_MS);
    }, WHISPER_CONFIG.chunkDurationMs - ROTATION_OVERLAP_MS);
  }

  private clearTimers(): void {
    if (this.rotationTimer !== null) {
      clearTimeout(this.rotationTimer);
      this.rotationTimer = null;
    }
    if (this.overlapStopTimer !== null) {
      clearTimeout(this.overlapStopTimer);
      this.overlapStopTimer = null;
    }
  }
}
